import { bottomNavigationBarVisibleAtom } from 'app/atoms';
import { withErrorBoundary } from 'app/components/error-boundary';
import { ToastProvider } from 'app/components/toast-provider';
import { BottomSheet } from 'app/design/primitives/bottom-sheet';
import { Column } from 'app/design/primitives/box';
import { Button } from 'app/design/primitives/button';
import { Modal } from 'app/design/primitives/modal';
import { Page, usePagePadding } from 'app/design/primitives/page';
import { Accessory } from 'app/design/primitives/page/accessory';
import { Text } from 'app/design/primitives/text';
import { useNavigationOptions } from 'app/hooks/navigation';
import { isDeletable, isDonationListing } from 'app/lib/listing';
import { ListingContext } from 'app/screens/shared/one-listing/components/ListingProvider';
import { EditListingForm, type EditListingActions } from 'app/screens/shared/edit-listing/components/edit-listing-form';
import { useListing } from 'app/screens/shared/one-listing/components/useListing';
import { OneListingSkeleton } from 'app/screens/shared/one-listing';
import { createTheme as createDSTheme } from 'app/shopify';
import { ListingType, type Listing } from 'app/types/listing';
import { WhiteLabel } from 'app/types/white-labels';
import type { NavigationScreen } from 'app/types/navigation';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import { isWhiteLabel } from 'app/utils/helpers';
import { useRegisterLoading } from 'app/utils/performance/screen-perf-context';
import { withOptionalMetrics } from 'app/utils/performance/with-metrics';
import { usePageTitle } from 'app/utils/web-helpers';
import { IconTrash } from '@tabler/icons-react-native';
import { useIsFocused } from '@react-navigation/native';
import { useSetAtom } from 'jotai';
import React, { useEffect, useRef } from 'react';

const theme = createDSTheme();

const EditListingScreenComponent: NavigationScreen<'edit-listing'> = ({ navigation, route }) => {
  const listingHook = useListing({ route });
  const { listingQuery, userQuery, canManage, t } = listingHook;
  const isFocused = useIsFocused();
  const setBottomNavigationBarVisible = useSetAtom(bottomNavigationBarVisibleAtom);

  useEffect(() => {
    setBottomNavigationBarVisible(!isFocused);
    return () => setBottomNavigationBarVisible(true);
  }, [isFocused, setBottomNavigationBarVisible]);

  const screenTitle = !listingQuery.data
    ? ''
    : t(`one-listing:title.${listingQuery.data.type}${isDonationListing(listingQuery.data) ? 'Donation' : ''}`);

  useNavigationOptions(navigation, {
    headerShown: true,
    headerBackVisible: false,
    headerTitle: () => (
      <Text variant="bodyXLargeMedium" color="white-label.header.on-header">
        {screenTitle}
      </Text>
    ),
  });

  const setPageTitle = usePageTitle();
  useEffect(
    () => listingQuery.data && setPageTitle(`${screenTitle} - {{name}}`, { name: listingQuery.data.item.name }),
    [listingQuery.data, screenTitle, setPageTitle],
  );

  useRegisterLoading(listingQuery.isPending, 'EditListingScreen');
  useRegisterLoading(userQuery.isPending, 'EditListingScreen');

  if (!listingQuery.data || userQuery.isPending) {
    return <OneListingSkeleton />;
  }

  if (!canManage) {
    return (
      <Page>
        <Text textAlign="center">{t('one-listing:unavailableMessage')}</Text>
      </Page>
    );
  }

  return <EditListingContent listingHook={listingHook} listing={listingQuery.data} />;
};

function EditListingContent({
  listingHook,
  listing,
}: {
  listingHook: ReturnType<typeof useListing>;
  listing: Listing;
}) {
  const { deleteListing, isAuthor, onDeletePress, setShowActions, showActions, t, updateListing, form } = listingHook;
  const defaultPaddingStyle = usePagePadding(true);
  const editListingActionsRef = useRef<EditListingActions | null>(null);
  const canDelete = isAuthor && isDeletable(listing);
  const deleteErrorMessage = (() => {
    if (!deleteListing.error) return undefined;
    const errorKey = `${deleteListing.error.message}.${listing.type as string}`;
    const translatedError = t(errorKey, { ns: 'api-errors' });
    return translatedError === errorKey ? deleteListing.error.message : translatedError;
  })();

  const isHauteGaspesie = isWhiteLabel(WhiteLabel.HauteGaspesie);
  const duplicatesValue = form.watch('duplicates');
  const duplicatesCount = typeof duplicatesValue === 'number' ? duplicatesValue : 0;
  const accessory = (
    <Accessory
      style={{
        paddingHorizontal: defaultPaddingStyle.paddingLeft,
        marginTop: theme.spacing['spacing.general.x2'],
      }}
    >
      <Column gap="spacing.general.x2">
        <Button
          authorize
          disabled={deleteListing.isPending}
          height={48}
          justifyContent="center"
          loading={updateListing.isPending}
          minHeight={48}
          onPress={() => editListingActionsRef.current?.save()}
          style={{
            backgroundColor: theme.colors['surface.inverse.default'],
            borderRadius: theme.borderRadii['radius.xl'],
          }}
          variant="primary"
        >
          <Text color="text.inverse.primary" fontWeight="700" textAlign="center" variant="bodyBase">
            {isHauteGaspesie && listing.type === ListingType.offer && duplicatesCount > 0
              ? t('one-listing:primaryButtonTextWithCopies', { count: duplicatesCount })
              : t('one-listing:primaryButtonText')}
          </Text>
        </Button>
        {canDelete ? (
          <Button
            authorize
            disabled={updateListing.isPending}
            height={48}
            justifyContent="center"
            loading={deleteListing.isPending}
            onPress={() => editListingActionsRef.current?.remove()}
            style={{ borderRadius: theme.borderRadii['radius.xl'] }}
            variant="delete"
          >
            <Text color="text.error" fontWeight="700" textAlign="center" variant="bodyBase">
              {listing.type === ListingType.request && (listing.status === 'available' || listing.status === 'expired')
                ? t('one-listing:removeButtonText')
                : t('one-listing:deleteButtonText')}
            </Text>
          </Button>
        ) : null}
      </Column>
    </Accessory>
  );

  return (
    <ListingContext.Provider value={listingHook}>
      <Page accessory={accessory} testID="edit-listing-screen" showsVerticalScrollIndicator={false}>
        <EditListingForm {...listing} actionsRef={editListingActionsRef} />
      </Page>

      {canDelete ? (
        <BottomSheet onClose={() => setShowActions(false)} open={showActions}>
          <BottomSheet.Action onPress={onDeletePress} prefix={<IconTrash strokeWidth={ICON_STROKE_WIDTH} />}>
            {t(`deleteConfirmation.${listing.type}`, { ns: 'one-listing' })}
          </BottomSheet.Action>
        </BottomSheet>
      ) : null}

      <Modal onClose={deleteListing.reset} open={Boolean(deleteListing.error)} transparent>
        <Modal.Card>
          <Modal.Title>{t(`deleteErrorModal.title.${listing.type as string}`, { ns: 'one-listing' })}</Modal.Title>
          <Column>
            <Text textAlign="center">{deleteErrorMessage}</Text>
          </Column>
          <Button marginBottom="spacing.general.x4" onPress={deleteListing.reset} variant="primary">
            {t('one-listing:deleteErrorModal.primaryButtonText', { ns: 'one-listing' })}
          </Button>
        </Modal.Card>
      </Modal>
      <ToastProvider />
    </ListingContext.Provider>
  );
}

export const EditListingScreen: NavigationScreen<'edit-listing'> = withOptionalMetrics(
  'EditListingScreen',
  withErrorBoundary(EditListingScreenComponent),
);
